/**
 * Per-contract custom sections ("Egna sektioner", ADR-0060 / ADR-0061) as
 * a block — the legacy `<Section>` atom's anchored siblings and the
 * frame-less `CustomSectionsBody`.
 *
 * Read from the reserved `__custom_sections` value. Normalisation mirrors
 * `normalizeCustomSections` in @hyranai/regta-templates, flat-shape
 * migration and caps included. A section renders as a numbered § with
 * ref `custom:<id>` (the legacy anchor ref); a named sub-section becomes a
 * numbered sub-clause (§ N.M), an unnamed one blends into the body.
 * Custom content is the user's own text, so it prints as text — not as
 * values — and survives a blank render, as in the legacy lane.
 */
import { br, details, p, section, t } from '@hyranai/regta-document-ir';
import { arr, str } from '../lib.js';
/** Reserved `values` key holding the contract's custom sections. */
export const CUSTOM_SECTIONS_KEY = '__custom_sections';
export const MAX_CUSTOM_SECTIONS = 20;
export const MAX_CUSTOM_SUBSECTIONS = 20;
export const MAX_CUSTOM_COMPONENTS = 20;
export const MAX_CUSTOM_ROWS = 50;
const MAX_TITLE_LEN = 200;
const MAX_KEY_LEN = 200;
const MAX_VALUE_LEN = 4000;
const MAX_BODY_LEN = 20000;
const clamp = (v, max) => (typeof v === 'string' ? v.slice(0, max) : '');
const idOf = (v) => (typeof v === 'string' && v ? v : '');
const obj = (v) => (v && typeof v === 'object' ? v : null);
function normalizeRows(value) {
    if (!Array.isArray(value))
        return [];
    return value.slice(0, MAX_CUSTOM_ROWS).flatMap((raw) => {
        const r = obj(raw);
        return r ? [{ key: clamp(r.key, MAX_KEY_LEN), value: clamp(r.value, MAX_VALUE_LEN) }] : [];
    });
}
function normalizeComponent(raw) {
    const r = obj(raw);
    const id = idOf(r?.id);
    if (!r || !id)
        return null;
    if (r.kind === 'text' || r.kind === 'paragraph')
        return { id, kind: 'text', body: clamp(r.body, MAX_BODY_LEN) };
    return { id, kind: 'key_value', rows: normalizeRows(r.rows) };
}
function normalizeSubSection(raw) {
    const r = obj(raw);
    const id = idOf(r?.id);
    if (!r || !id)
        return null;
    const components = Array.isArray(r.components)
        ? r.components.slice(0, MAX_CUSTOM_COMPONENTS).map(normalizeComponent).filter((c) => c !== null)
        : [];
    const title = clamp(r.title, MAX_TITLE_LEN);
    return title ? { id, title, components } : { id, components };
}
/** The defensive read boundary, including the pre-redesign flat `{ kind, rows, body }` migration. */
export function normalizeCustomSections(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value) {
        if (out.length >= MAX_CUSTOM_SECTIONS)
            break;
        const r = obj(raw);
        const id = idOf(r?.id);
        if (!r || !id)
            continue;
        const title = clamp(r.title, MAX_TITLE_LEN);
        const afterRef = typeof r.afterRef === 'string' && r.afterRef ? r.afterRef : undefined;
        let subSections;
        if (!Array.isArray(r.subSections) && (r.kind !== undefined || r.rows !== undefined || r.body !== undefined)) {
            const component = r.kind === 'paragraph' || r.kind === 'text'
                ? { id: `${id}-c`, kind: 'text', body: clamp(r.body, MAX_BODY_LEN) }
                : { id: `${id}-c`, kind: 'key_value', rows: normalizeRows(r.rows) };
            subSections = [{ id: `${id}-s`, components: [component] }];
        }
        else {
            subSections = Array.isArray(r.subSections)
                ? r.subSections.slice(0, MAX_CUSTOM_SUBSECTIONS).map(normalizeSubSection).filter((s) => s !== null)
                : [];
        }
        out.push(afterRef ? { id, title, afterRef, subSections } : { id, title, subSections });
    }
    return out;
}
const componentHasContent = (c) => c.kind === 'text' ? c.body.trim() !== '' : c.rows.some((r) => r.key.trim() !== '' || r.value.trim() !== '');
const subSectionHasContent = (ss) => (ss.title ?? '').trim() !== '' || ss.components.some(componentHasContent);
/** Blank mode shows every section unpruned; otherwise empty parts drop out (legacy `readCustomSections`). */
export function readCustomSections(value, mode) {
    const all = normalizeCustomSections(value);
    if (mode === 'blank')
        return all;
    return all
        .filter((cs) => cs.title.trim() !== '' || cs.subSections.some(subSectionHasContent))
        .map((cs) => ({
        ...cs,
        subSections: cs.subSections
            .map((ss) => ({ ...ss, components: ss.components.filter(componentHasContent) }))
            .filter((ss) => (ss.title ?? '').trim() !== '' || ss.components.length > 0),
    }));
}
/**
 * The legacy anchor rule: set and valid → itself; unset → the default
 * placement (`defaultAfterRef`, else before the final declared section);
 * stale → the last declared section, so content is never lost.
 */
export function resolveCustomAfterRef(cs, declaredRefs, defaultAfterRef) {
    if (declaredRefs.length === 0)
        return undefined;
    const last = declaredRefs[declaredRefs.length - 1];
    if (cs.afterRef === undefined) {
        if (defaultAfterRef && declaredRefs.includes(defaultAfterRef))
            return defaultAfterRef;
        return declaredRefs[declaredRefs.length - 2] ?? last;
    }
    return declaredRefs.includes(cs.afterRef) ? cs.afterRef : last;
}
/** `custom:<id>` — the section ref of a custom section (legacy `customSectionAnchorRef`). */
export const customSectionRef = (id) => `custom:${id}`;
/** Text with its line breaks kept (legacy `whitespace-pre-wrap`). */
export function textWithBreaks(body) {
    return body.split('\n').flatMap((line, i) => [...(i > 0 ? [br()] : []), ...(line === '' ? [] : [t(line)])]);
}
function componentNodes(c) {
    if (c.kind === 'text')
        return c.body.trim() === '' ? [] : [p(textWithBreaks(c.body))];
    const filled = c.rows.filter((r) => r.key.trim() !== '' || r.value.trim() !== '');
    return filled.length ? [details(filled.map((r) => [r.key, r.value]))] : [];
}
/** One custom section as a numbered section with its sub-clauses. */
export function customSectionNode(cs) {
    const ref = customSectionRef(cs.id);
    const content = cs.subSections.flatMap((ss) => {
        const inner = ss.components.flatMap(componentNodes);
        const title = (ss.title ?? '').trim();
        return title !== '' ? [section(`${ref}:${ss.id}`, ss.title, inner, { keepTogether: true })] : inner;
    });
    return section(ref, cs.title.trim() === '' ? undefined : cs.title, content, { keepTogether: true });
}
export function customSectionsBlockDefinition(blockKey, version = 'v1') {
    return {
        blockKey,
        version,
        plane: 'tenant',
        status: 'published',
        language: 'sv-SE',
        title: 'Egna sektioner',
        summary: 'The contract’s own sections (ADR-0060): numbered §, optional named sub-clauses (§ N.M) of free text or label/value rows, placed after a declared section.',
        slots: [
            {
                key: CUSTOM_SECTIONS_KEY,
                label: 'Egna sektioner',
                type: 'list',
                repeats: true,
                help: 'Sektioner med egen rubrik, underrubriker och innehåll i form av fritext eller uppgiftsrader.',
            },
        ],
        compile: (input, ctx) => {
            const all = readCustomSections(input[CUSTOM_SECTIONS_KEY], ctx.mode);
            const afterRef = str(input.after_ref) ?? str(input.anchor);
            const declared = arr(input.declared_refs);
            const defaultAfterRef = str(input.default_after_ref);
            const placed = afterRef ? all.filter((cs) => resolveCustomAfterRef(cs, declared, defaultAfterRef) === afterRef) : all;
            return placed.length === 0 ? null : placed.map(customSectionNode);
        },
    };
}
/** `common.custom-sections@v1`. */
export const customSectionsBlock = customSectionsBlockDefinition('common.custom-sections');
/**
 * Each declared section followed by its custom-sections anchor
 * (`custom-after-<ref>`), sharing the section's gate so customs anchored to
 * a hidden section hide with it — as the legacy `<Section>` did.
 */
export function declaredSectionInstances(sections, opts = {}) {
    const declaredRefs = sections.map((s) => s.ref);
    const customBlockRef = opts.customBlockRef ?? 'common.custom-sections@v1';
    return sections.flatMap((s) => {
        const shared = { ...(s.includeWhen ? { includeWhen: s.includeWhen } : {}), ...(s.part ? { part: s.part } : {}) };
        return [
            ...(s.before ?? []),
            {
                instanceId: s.instanceId ?? s.ref,
                blockRef: s.blockRef,
                input: s.besidePrevious ? { ...(s.input ?? {}), beside_previous: true } : (s.input ?? {}),
                ...shared,
            },
            {
                instanceId: `custom-after-${s.ref}`,
                blockRef: customBlockRef,
                input: {
                    after_ref: s.ref,
                    declared_refs: declaredRefs,
                    ...(opts.defaultAfterRef ? { default_after_ref: opts.defaultAfterRef } : {}),
                },
                ...shared,
            },
        ];
    });
}
//# sourceMappingURL=custom-sections.js.map